/**
 * Site-search shared types.
 *
 * One `SearchDoc` per indexable thing — a static page, a CMS biomarker, a
 * disease, an article, etc. Build-time script (`scripts/build-search-index.ts`)
 * emits an array of these per locale-file to `public/search-index/{file}.json`;
 * the client lazy-loads the JSON for the active locale and queries it via
 * `lib/search/engine.ts`.
 *
 * Bodies are pre-tokenized at build time so the runtime engine can score
 * with simple Set lookups — no MiniSearch dependency for v1. If we outgrow
 * lexical matching (or the corpus blows past ~1k docs per locale), swap the
 * engine behind the same `search()` signature.
 */

export type SearchDocType =
  | "page" // static page (home, pricing, about, …)
  | "biomarker"
  | "disease"
  | "test" // /tests/<slug>
  | "faq"
  | "insight" // blog post
  | "location"
  | "author"
  | "career" // job listing
  | "symptom" // /symptoms/<slug>

export interface SearchDoc {
  /** Stable id, unique within a locale-file index. e.g. `page:pricing`, `biomarker:ferritin`. */
  id: string
  /** Site-relative URL, including the country/locale prefix. e.g. `/ch/en/pricing`. */
  url: string
  /** User-facing title. */
  title: string
  /** 1–2 sentence blurb shown under the title in results. */
  excerpt: string
  /**
   * Pre-tokenized body — lowercased, stop-words dropped, deduplicated,
   * space-joined. Engine matches against this directly with `indexOf`/Set.
   */
  bodyTokens: string
  type: SearchDocType
  /**
   * Optional category label (e.g. biomarker category, disease category).
   * Surfaced in result rows so users can disambiguate same-named items.
   */
  category?: string
}

export interface SearchIndex {
  /** Schema version — bump when SearchDoc shape changes so stale clients can retry. */
  version: number
  /** Locale-file (e.g. "en", "de", "en-NL"). One index per file. */
  locale: string
  /** ISO 8601 build timestamp. Useful for cache-busting. */
  builtAt: string
  docs: SearchDoc[]
}

export const SEARCH_INDEX_VERSION = 1
